'use client';

/* NEEDS TO BE A CLIENT COMPONENT BECAUSE IT IS USED IN THE ADD REVIEW FORM */

import { useIntl } from 'react-intl';
import { Card, CardContent, CardHeader } from '@packages/shared/src/ui/Card/Card';
import { type FragmentType } from '@packages/gql/generated/shopping/fragment-masking';
import type { CustomerReviewCardFragmentFragmentDoc } from '@packages/gql/generated/shopping/CustomerReviewCardFragmentFragmentDoc';
import { unmask } from '@packages/gql/src/betterMasking';
import { RatingStars } from '@packages/shared/src/ui/Rating/RatingStars';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { CustomerReviewResponse } from '../CustomerReviewResponse/CustomerReviewResponse';
import { CustomerReviewTags } from '../CustomerReviewTags/CustomerReviewTags';
import { CustomerReviewCardActionsMenu } from '../CustomerReviewCardActionsMenu/CustomerReviewCardActionsMenu.client';
import { Verified } from '../Verified/Verified.client';
import { AuthorAndCreatedWrapper } from './AuthorAndCreatedWrapper/AuthorAndCreatedWrapper.client';

/* GraphQL */ `
  fragment CustomerReviewCardFragment on RatingReview {
    id
    created
    description
    isVerified
    rating
    reviewer {
      nickname
    }
    reviewTags {
      proTags {
        ...CustomerReviewTagFragment
      }
      contraTags {
        ...CustomerReviewTagFragment
      }
    }
    title
    responses {
      ...CustomerReviewResponseFragment
    }
  }
`;

export type CustomerReviewCardProps = {
  /**
   * data - the data needed to render the component
   */
  data: FragmentType<typeof CustomerReviewCardFragmentFragmentDoc & { isVerified: boolean }>;
  /**
   * hideActionMenu - should the actions menu in the header be hidden
   */
  hideActionsMenu?: boolean;
};

/**
 * The CustomerReviewCard component displays a product review written by a customer
 */
export const CustomerReviewCard = ({ data, hideActionsMenu }: CustomerReviewCardProps) => {
  const { formatMessage } = useIntl();
  const {
    customerRatings: { maxRating },
  } = useConfig();

  const { id, reviewer, rating, created, title, description, responses, reviewTags, isVerified } =
    unmask<typeof CustomerReviewCardFragmentFragmentDoc & { isVerified: boolean }>(data);

  return (
    <div>
      <Card>
        <CardHeader hasDivider>
          <div className="flex w-full flex-row flex-nowrap justify-between gap-1">
            <div className="mt-1">
              <div className="flex flex-row gap-1">
                <RatingStars
                  role="img"
                  ratingValue={rating}
                  aria-label={formatMessage(
                    {
                      id: 'customerReviews.reviewCard.ratingIcons.aria.label',
                      defaultMessage: 'Produktbewertung {ratingValue} von {maxRating} Sternen',
                    },
                    {
                      ratingValue: rating,
                      maxRating,
                    },
                  )}
                />
                {isVerified ? <Verified /> : null}
              </div>
              <div className="text-size-body-xs text-color-text-darktransparent mt-1">
                <AuthorAndCreatedWrapper authorNickName={reviewer.nickname} createdDate={created} />
              </div>
            </div>
            {!hideActionsMenu && <CustomerReviewCardActionsMenu ratingId={id} />}
          </div>
        </CardHeader>
        <CardContent className="py-2">
          <div className="mt-1 flex flex-col gap-1">
            <div className="text-size-body-sm">
              <div className="font-bold">{title}</div>
              <div className="whitespace-pre-wrap">{description}</div>
            </div>
            {reviewTags?.proTags?.length ? (
              <CustomerReviewTags
                data={reviewTags.proTags}
                label={formatMessage({
                  id: 'customerReviews.reviewCard.tag.pro.label',
                  defaultMessage: 'Vorteile:',
                })}
              />
            ) : null}
            {reviewTags?.contraTags?.length ? (
              <CustomerReviewTags
                data={reviewTags.contraTags}
                label={formatMessage({
                  id: 'customerReviews.reviewCard.tag.contra.label',
                  defaultMessage: 'Nachteile:',
                })}
              />
            ) : null}
            {responses?.map((response, i) => (
              <CustomerReviewResponse
                data={response}
                label={formatMessage({
                  id: 'customerReviews.reviewCard.response.title',
                  defaultMessage: 'Antwort von BAUR',
                })}
                // eslint-disable-next-line react/no-array-index-key
                key={`${id}CustomerReviewResponse${i}`}
              />
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
